import { Button, IconButton, IconType, Modal } from '@affine/component';
import { getStoreManager } from '@affine/core/blocksuite/manager/store';
import { useGuard } from '@affine/core/components/guard/use-guard';
import { useAsyncCallback } from '@affine/core/components/hooks/affine-async-hooks';
import { useNavigateHelper } from '@affine/core/components/hooks/use-navigate-helper';
import { defaultWorkspaceApiClient } from '@affine/core/modules/cloud';
import {
  type DialogComponentProps,
  GlobalDialogService,
  type WORKSPACE_DIALOG_SCHEMA,
} from '@affine/core/modules/dialogs';
import { DocsService } from '@affine/core/modules/doc';
import { ExplorerIconService } from '@affine/core/modules/explorer-icon/services/explorer-icon';
import { OrganizeService } from '@affine/core/modules/organize';
import {
  getStreamLainWorkspaceSchema,
  type WorkspaceMetadata,
  WorkspaceService,
} from '@affine/core/modules/workspace';
import { DebugLogger } from '@affine/debug';
import { useI18n } from '@affine/i18n';
import track from '@affine/track';
import { openFilesWith } from '@blocksuite/affine/shared/utils';
import type { ExtensionType, Store, Workspace } from '@blocksuite/affine/store';
import {
  DocxTransformer,
  HTMLImportTooComplexError,
  HtmlTransformer,
  MarkdownTransformer,
  NotionHtmlTransformer,
  ZipTransformer,
} from '@blocksuite/affine/widgets/linked-doc';
import {
  CloseIcon,
  ExportToHtmlIcon,
  ExportToMarkdownIcon,
  FileIcon,
  HelpIcon,
  NotionIcon,
  PageIcon,
  SaveIcon,
  ZipIcon,
} from '@blocksuite/icons/rc';
import {
  type DatabaseShardManifest,
  normalizeDatabaseShardManifest,
} from '@streamlain/database-identity';
import { cssVarV2 } from '@streamlain/theme/v2';
import { useService } from '@toeverything/infra';
import React, {
  type ReactElement,
  type SVGAttributes,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  materializeSnapshotImport,
  normalizeSnapshotImportStores,
  snapshotImportPageDocIds,
  snapshotImportSystemDocIds,
} from '../../../modules/doc/services/snapshot-import-normalization';
import {
  cancelDatabaseImportJob,
  type DatabaseImportJob,
  type DatabaseImportJobResult,
  type DatabaseImportLocalFirstResult,
  type DatabaseSystemDocAccessBinding,
  importCsvFilesLocalFirst,
  importCsvFilesWithDatabaseImportJob,
  importExcelFilesLocalFirst,
  importExcelFilesWithDatabaseImportJob,
  type LocalShardedDatabaseImportProgress,
  type LocalShardedDatabaseImportProgressPhase,
  postDatabaseSystemDocAccessBindings,
  retryDatabaseImportJob,
  throwIfLocalDatabaseImportAborted,
  waitForDatabaseImportJobs,
} from '../../../modules/streamlain/file-import';
import { DatabaseImportParserWorkerUnavailableError } from '../../../modules/streamlain/file-import/database-import-parser-worker-client';
import { NotionImporter } from '../../../modules/streamlain/notion-import';
import { type CsvImportConfig, CsvImportPreview } from './csv-preview';
import {
  databaseImportRuntimeFromWorkspaceFlavour,
  resolveDatabaseImportRoute,
} from './database-import-route';
import { type ExcelImportConfig, ExcelImportPreview } from './excel-preview';
import { type NotionImportConfig, NotionImportPreview } from './notion-preview';
import * as style from './styles.css';

const logger = new DebugLogger('import');

async function preflightSnapshotImportDocs(input: {
  workspaceId: string;
  pageDocIds: readonly string[];
  systemDocIds: readonly string[];
}): Promise<void> {
  const path = `/api/workspace/${encodeURIComponent(input.workspaceId)}/import-docs/preflight`;
  const response = await defaultWorkspaceApiClient.request({
    workspaceId: input.workspaceId,
    path,
    init: {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        pageDocIds: input.pageDocIds,
        systemDocIds: input.systemDocIds,
      }),
    },
    category: 'workspace-api',
    key: 'snapshot-import-docs-preflight',
    mode: 'foreground',
    priority: 'foreground',
  });
  if (!response.ok) {
    throw new Error(
      await response.text().catch(() => 'Snapshot import preflight failed')
    );
  }
}

type YMapLike = {
  get(key: string): unknown;
  forEach(callback: (value: unknown, key: string) => void): void;
};

type LocalFirstDatabaseImportDocEngine = {
  addDoc?: (docId: string, source: 'import') => void;
  flushAndWaitForSynced?: (docId: string) => Promise<void>;
};

function isYMapLike(value: unknown): value is YMapLike {
  return (
    !!value &&
    typeof value === 'object' &&
    typeof (value as YMapLike).get === 'function' &&
    typeof (value as YMapLike).forEach === 'function'
  );
}

function collectImportedDatabaseSystemDocAccessBindings(
  workspaceId: string,
  stores: readonly Store[]
): DatabaseSystemDocAccessBinding[] {
  const rootManifests = new Map<string, DatabaseShardManifest>();

  for (const store of stores) {
    if (!store.id.startsWith('db$')) continue;
    const blocks = store.spaceDoc.getMap('blocks');
    blocks.forEach(value => {
      if (!isYMapLike(value)) return;
      if (value.get('sys:flavour') !== 'affine:database') return;
      const manifest = normalizeDatabaseShardManifest(
        value.get('prop:databaseShardManifest')
      );
      if (!manifest || manifest.workspaceId !== workspaceId) return;
      rootManifests.set(manifest.databaseRootDocId, manifest);
    });
  }

  const bindings: DatabaseSystemDocAccessBinding[] = [];
  for (const store of stores) {
    if (store.id.startsWith('db$')) continue;
    const blocks = store.spaceDoc.getMap('blocks');
    blocks.forEach((value, blockId) => {
      if (!isYMapLike(value)) return;
      if (value.get('sys:flavour') !== 'affine:database') return;
      const rootDocId = value.get('prop:databaseRootDocId');
      if (typeof rootDocId !== 'string') return;
      const manifest = rootManifests.get(rootDocId);
      if (!manifest) return;
      bindings.push({
        databaseId: manifest.databaseId,
        rootDocId,
        accessDocId: store.id,
        blockId,
        manifest,
      });
    });
  }

  return bindings;
}

async function registerImportedDatabaseSystemDocAccess(
  workspaceId: string,
  stores: readonly Store[]
) {
  const bindings = collectImportedDatabaseSystemDocAccessBindings(
    workspaceId,
    stores
  );
  if (bindings.length === 0) return;

  await postDatabaseSystemDocAccessBindings({ workspaceId, bindings });
}

function getLoadedImportStore(
  collection: Workspace,
  docId: string,
  extensions: ExtensionType[]
): Store {
  const doc = collection.getDoc(docId);
  if (!doc) {
    throw new Error(`Imported database document "${docId}" is not available.`);
  }
  doc.load();
  return doc.getStore({ id: docId, extensions });
}

function readImportedDatabaseManifest(
  rootStore: Store,
  databaseId: string,
  rootDocId: string
): DatabaseShardManifest {
  const rootBlock = rootStore.spaceDoc.getMap('blocks').get(databaseId);
  if (!isYMapLike(rootBlock)) {
    throw new Error(
      `Imported database root "${rootDocId}" does not contain database "${databaseId}".`
    );
  }
  const manifest = normalizeDatabaseShardManifest(
    rootBlock.get('prop:databaseShardManifest')
  );
  if (!manifest) {
    throw new Error(
      `Imported database "${databaseId}" root "${rootDocId}" has no row-shard manifest.`
    );
  }
  return manifest;
}

function collectLocalFirstDatabaseImportSyncPlan(input: {
  collection: Workspace;
  databaseBlockIds: readonly string[];
  docId: string;
  extensions: ExtensionType[];
  workspaceId: string;
}): { bindings: DatabaseSystemDocAccessBinding[]; docIds: string[] } {
  const hostStore = getLoadedImportStore(
    input.collection,
    input.docId,
    input.extensions
  );
  const hostBlocks = hostStore.spaceDoc.getMap('blocks');
  const bindings: DatabaseSystemDocAccessBinding[] = [];
  const docIds = new Set<string>([input.workspaceId, input.docId]);

  for (const blockId of input.databaseBlockIds) {
    const hostBlock = hostBlocks.get(blockId);
    if (!isYMapLike(hostBlock)) {
      throw new Error(
        `Imported database host block "${blockId}" is not available.`
      );
    }
    if (hostBlock.get('sys:flavour') !== 'affine:database') {
      throw new Error(`Imported block "${blockId}" is not a database.`);
    }

    const databaseIdValue = hostBlock.get('prop:databaseId');
    const databaseId =
      typeof databaseIdValue === 'string' && databaseIdValue
        ? databaseIdValue
        : blockId;
    const rootDocId = hostBlock.get('prop:databaseRootDocId');
    if (typeof rootDocId !== 'string' || !rootDocId) {
      throw new Error(`Imported database "${databaseId}" has no root doc id.`);
    }

    docIds.add(rootDocId);
    const rootStore = getLoadedImportStore(
      input.collection,
      rootDocId,
      input.extensions
    );
    const manifest = readImportedDatabaseManifest(
      rootStore,
      databaseId,
      rootDocId
    );
    bindings.push({
      databaseId: manifest.databaseId,
      rootDocId,
      accessDocId: input.docId,
      blockId,
      manifest,
    });

    for (const shard of manifest.shards) {
      if (!shard.serverDocId) continue;
      getLoadedImportStore(
        input.collection,
        shard.serverDocId,
        input.extensions
      );
      docIds.add(shard.serverDocId);
    }
  }

  return { bindings, docIds: Array.from(docIds) };
}

async function flushLocalFirstDatabaseImport(input: {
  collection: Workspace;
  docEngine: LocalFirstDatabaseImportDocEngine;
  docId: string;
  extensions: ExtensionType[];
  result: DatabaseImportLocalFirstResult;
  signal?: AbortSignal;
  workspaceId: string;
}) {
  if (input.result.databaseBlockIds.length === 0) return;
  if (!input.docEngine.flushAndWaitForSynced) {
    throw new Error('Local database import sync engine is not available.');
  }

  const plan = collectLocalFirstDatabaseImportSyncPlan({
    collection: input.collection,
    databaseBlockIds: input.result.databaseBlockIds,
    docId: input.docId,
    extensions: input.extensions,
    workspaceId: input.workspaceId,
  });

  for (const docId of plan.docIds) {
    throwIfLocalDatabaseImportAborted(input.signal);
    input.docEngine.addDoc?.(docId, 'import');
  }
  for (const docId of plan.docIds) {
    throwIfLocalDatabaseImportAborted(input.signal);
    await input.docEngine.flushAndWaitForSynced(docId);
  }
  throwIfLocalDatabaseImportAborted(input.signal);
  await input.docEngine.flushAndWaitForSynced(input.workspaceId);

  await postDatabaseSystemDocAccessBindings({
    workspaceId: input.workspaceId,
    bindings: plan.bindings,
    signal: input.signal,
  });
}

function isDatabaseImportLocalFirstResult(
  result: DatabaseImportJobResult | DatabaseImportLocalFirstResult
): result is DatabaseImportLocalFirstResult {
  return Array.isArray(
    (result as { databaseBlockIds?: unknown }).databaseBlockIds
  );
}

type NotionPageIcon = {
  type: 'emoji' | 'image';
  content: string; // emoji unicode or image URL/data
};

type FolderHierarchy = {
  name: string;
  path: string;
  children: Map<string, FolderHierarchy>;
  pageId?: string;
  parentPath?: string;
  icon?: NotionPageIcon;
};

// Helper function to create folder structure using OrganizeService
function createFolderStructure(
  organizeService: OrganizeService,
  hierarchy: FolderHierarchy,
  parentFolderId: string | null = null,
  explorerIconService?: ExplorerIconService
): {
  folderId: string | null;
  docLinks: Array<{ folderId: string; docId: string }>;
} {
  const docLinks: Array<{ folderId: string; docId: string }> = [];
  const rootFolder = organizeService.folderTree.rootFolder;

  function processHierarchyNode(
    node: FolderHierarchy,
    currentParentId: string | null
  ): string | null {
    let currentFolderId = currentParentId;

    // If this node represents a folder (has children but no pageId), create it
    if (node.children.size > 0 && !node.pageId && node.name) {
      const parent = currentParentId
        ? organizeService.folderTree.folderNode$(currentParentId).value
        : rootFolder;

      if (parent) {
        const index = parent.indexAt('after');
        currentFolderId = parent.createFolder(node.name, index);
      }
    }

    // Process all children
    for (const child of node.children.values()) {
      if (child.pageId) {
        // This is a document, link it to the current folder
        if (currentFolderId) {
          docLinks.push({ folderId: currentFolderId, docId: child.pageId });
        }

        // Set icon for the document if available
        if (child.icon && explorerIconService) {
          logger.debug('=== Setting icon for document ===');
          logger.debug('Document ID:', child.pageId);
          logger.debug('Icon data:', child.icon);

          try {
            let iconData;
            if (child.icon.type === 'emoji') {
              iconData = {
                type: IconType.Emoji as const,
                unicode: child.icon.content,
              };
              logger.debug('Created emoji icon data:', iconData);
            } else if (child.icon.type === 'image') {
              // For image icons, we'd need to handle blob conversion
              // For now, let's skip image icons or convert them to default
              // This could be enhanced later to download and convert images to blobs
              logger.debug(
                'Skipping image icon (not implemented):',
                child.icon.content
              );
              iconData = undefined;
            }

            if (iconData) {
              logger.debug('Calling explorerIconService.setIcon with:', {
                where: 'doc',
                id: child.pageId,
                icon: iconData,
              });
              explorerIconService.setIcon({
                where: 'doc',
                id: child.pageId,
                icon: iconData,
              });
              logger.debug('Icon set successfully for document:', child.pageId);
            } else {
              logger.debug('No valid icon data to set');
            }
          } catch (error) {
            logger.error(
              'Error setting icon for document:',
              child.pageId,
              error
            );
            logger.warn(
              'Failed to set icon for document:',
              child.pageId,
              error
            );
          }
        } else {
          if (!child.icon) {
            logger.debug('No icon found for document:', child.pageId);
          }
          if (!explorerIconService) {
            logger.debug(
              'ExplorerIconService not available for document:',
              child.pageId
            );
          }
        }
      } else if (child.children.size > 0) {
        // This is a subfolder, process it recursively
        processHierarchyNode(child, currentFolderId);
      }
    }

    return currentFolderId;
  }

  const rootFolderId = processHierarchyNode(hierarchy, parentFolderId);
  return { folderId: rootFolderId, docLinks };
}

type ImportType =
  | 'markdown'
  | 'markdownZip'
  | 'notion'
  | 'notionEnhanced'
  | 'snapshot'
  | 'html'
  | 'docx'
  | 'csv'
  | 'excel'
  | 'dotaffinefile';
type AcceptType =
  | 'Markdown'
  | 'Zip'
  | 'Html'
  | 'Docx'
  | 'Csv'
  | 'Excel'
  | 'Skip'; // Skip is used for dotaffinefile
// E013-S003: Added 'notionPreview' status for preview before import
type Status =
  | 'idle'
  | 'notionPreview'
  | 'csvPreview'
  | 'excelPreview'
  | 'importing'
  | 'success'
  | 'error';
type ImportResult = {
  docIds: string[];
  systemDocIds?: string[];
  databaseBlockIds?: string[];
  entryId?: string;
  isWorkspaceFile?: boolean;
  rootFolderId?: string;
};

type FrontendImportProgress = Omit<
  LocalShardedDatabaseImportProgress,
  'phase'
> & {
  phase: LocalShardedDatabaseImportProgressPhase | 'snapshot-importing';
};

type ImportConfig = {
  fileOptions: { acceptType: AcceptType; multiple: boolean };
  importFunction: (
    docCollection: Workspace,
    files: File[],
    handleImportAffineFile: () => Promise<WorkspaceMetadata | undefined>,
    organizeService?: OrganizeService,
    explorerIconService?: ExplorerIconService,
    onProgress?: (progress: FrontendImportProgress) => void
  ) => Promise<ImportResult>;
};

const importOptions = [
  {
    key: 'markdown',
    label: 'com.streamlain.import.markdown-files',
    prefixIcon: (
      <ExportToMarkdownIcon
        color={cssVarV2('icon/primary')}
        width={20}
        height={20}
      />
    ),
    testId: 'editor-option-menu-import-markdown-files',
    type: 'markdown' as ImportType,
  },
  {
    key: 'markdownZip',
    label: 'com.streamlain.import.markdown-with-media-files',
    prefixIcon: (
      <ZipIcon color={cssVarV2('icon/primary')} width={20} height={20} />
    ),
    suffixIcon: (
      <HelpIcon color={cssVarV2('icon/primary')} width={20} height={20} />
    ),
    suffixTooltip: 'com.streamlain.import.markdown-with-media-files.tooltip',
    testId: 'editor-option-menu-import-markdown-with-media',
    type: 'markdownZip' as ImportType,
  },
  {
    key: 'html',
    label: 'com.streamlain.import.html-files',
    prefixIcon: (
      <ExportToHtmlIcon
        color={cssVarV2('icon/primary')}
        width={20}
        height={20}
      />
    ),
    suffixIcon: (
      <HelpIcon color={cssVarV2('icon/primary')} width={20} height={20} />
    ),
    suffixTooltip: 'com.streamlain.import.html-files.tooltip',
    testId: 'editor-option-menu-import-html',
    type: 'html' as ImportType,
  },
  {
    key: 'notion',
    label: 'com.streamlain.import.notion-standard',
    prefixIcon: (
      <NotionIcon color={cssVarV2('icon/primary')} width={20} height={20} />
    ),
    suffixIcon: (
      <HelpIcon color={cssVarV2('icon/primary')} width={20} height={20} />
    ),
    suffixTooltip: 'com.streamlain.import.notion-standard.tooltip',
    testId: 'editor-option-menu-import-notion',
    type: 'notion' as ImportType,
  },
  {
    key: 'notionEnhanced',
    label: 'com.streamlain.import.notion-enhanced',
    prefixIcon: (
      <NotionIcon color={cssVarV2('icon/primary')} width={20} height={20} />
    ),
    suffixIcon: (
      <HelpIcon color={cssVarV2('icon/primary')} width={20} height={20} />
    ),
    suffixTooltip: 'com.streamlain.import.notion-enhanced.tooltip',
    testId: 'editor-option-menu-import-notion-enhanced',
    type: 'notionEnhanced' as ImportType,
  },
  {
    key: 'csv',
    label: 'com.streamlain.import.csv-files',
    prefixIcon: (
      <FileIcon color={cssVarV2('icon/primary')} width={20} height={20} />
    ),
    suffixIcon: (
      <HelpIcon color={cssVarV2('icon/primary')} width={20} height={20} />
    ),
    suffixTooltip: 'com.streamlain.import.csv-files.tooltip',
    testId: 'editor-option-menu-import-csv',
    type: 'csv' as ImportType,
  },
  {
    key: 'excel',
    label: 'com.streamlain.import.excel-files',
    prefixIcon: (
      <FileIcon color={cssVarV2('icon/primary')} width={20} height={20} />
    ),
    suffixIcon: (
      <HelpIcon color={cssVarV2('icon/primary')} width={20} height={20} />
    ),
    suffixTooltip: 'com.streamlain.import.excel-files.tooltip',
    testId: 'editor-option-menu-import-excel',
    type: 'excel' as ImportType,
  },
  {
    key: 'docx',
    label: 'com.streamlain.import.docx',
    prefixIcon: (
      <FileIcon color={cssVarV2('icon/primary')} width={20} height={20} />
    ),
    suffixIcon: (
      <HelpIcon color={cssVarV2('icon/primary')} width={20} height={20} />
    ),
    suffixTooltip: 'com.streamlain.import.docx.tooltip',
    testId: 'editor-option-menu-import-docx',
    type: 'docx' as ImportType,
  },
  {
    key: 'snapshot',
    label: 'com.streamlain.import.snapshot',
    prefixIcon: (
      <PageIcon color={cssVarV2('icon/primary')} width={20} height={20} />
    ),
    suffixIcon: (
      <HelpIcon color={cssVarV2('icon/primary')} width={20} height={20} />
    ),
    suffixTooltip: 'com.streamlain.import.snapshot.tooltip',
    testId: 'editor-option-menu-import-snapshot',
    type: 'snapshot' as ImportType,
  },
  BUILD_CONFIG.isElectron
    ? {
        key: 'dotaffinefile',
        label: 'com.streamlain.import.dotaffinefile',
        prefixIcon: (
          <SaveIcon color={cssVarV2('icon/primary')} width={20} height={20} />
        ),
        suffixIcon: (
          <HelpIcon color={cssVarV2('icon/primary')} width={20} height={20} />
        ),
        suffixTooltip: 'com.streamlain.import.dotaffinefile.tooltip',
        testId: 'editor-option-menu-import-dotaffinefile',
        type: 'dotaffinefile' as ImportType,
      }
    : null,
].filter(v => v !== null);

const importConfigs: Record<ImportType, ImportConfig> = {
  markdown: {
    fileOptions: { acceptType: 'Markdown', multiple: true },
    importFunction: async (
      docCollection,
      files,
      _handleImportAffineFile,
      _organizeService,
      _explorerIconService
    ) => {
      const docIds: string[] = [];
      for (const file of files) {
        const text = await file.text();
        const fileName = file.name.split('.').slice(0, -1).join('.');
        const docId = await MarkdownTransformer.importMarkdownToDoc({
          collection: docCollection,
          schema: getStreamLainWorkspaceSchema(),
          markdown: text,
          fileName,
          extensions: getStoreManager().config.init().value.get('store'),
        });
        if (docId) docIds.push(docId);
      }
      return {
        docIds,
      };
    },
  },
  markdownZip: {
    fileOptions: { acceptType: 'Zip', multiple: false },
    importFunction: async (
      docCollection,
      files,
      _handleImportAffineFile,
      _organizeService,
      _explorerIconService
    ) => {
      const file = files.length === 1 ? files[0] : null;
      if (!file) {
        throw new Error('Expected a single zip file for markdownZip import');
      }
      const docIds = await MarkdownTransformer.importMarkdownZip({
        collection: docCollection,
        schema: getStreamLainWorkspaceSchema(),
        imported: file,
        extensions: getStoreManager().config.init().value.get('store'),
      });
      return {
        docIds,
      };
    },
  },
  html: {
    fileOptions: { acceptType: 'Html', multiple: true },
    importFunction: async (
      docCollection,
      files,
      _handleImportAffineFile,
      _organizeService,
      _explorerIconService
    ) => {
      const docIds: string[] = [];
      for (const file of files) {
        const text = await file.text();
        const fileName = file.name.split('.').slice(0, -1).join('.');
        const docId = await HtmlTransformer.importHTMLToDoc({
          collection: docCollection,
          schema: getStreamLainWorkspaceSchema(),
          extensions: getStoreManager().config.init().value.get('store'),
          html: text,
          fileName,
        });
        if (docId) docIds.push(docId);
      }
      return {
        docIds,
      };
    },
  },
  notion: {
    fileOptions: { acceptType: 'Zip', multiple: false },
    importFunction: async (
      docCollection,
      files,
      _handleImportAffineFile,
      organizeService,
      explorerIconService
    ) => {
      const file = files.length === 1 ? files[0] : null;
      if (!file) {
        throw new Error('Expected a single zip file for notion import');
      }
      const { entryId, pageIds, isWorkspaceFile, folderHierarchy } =
        await NotionHtmlTransformer.importNotionZip({
          collection: docCollection,
          schema: getStreamLainWorkspaceSchema(),
          imported: file,
          extensions: getStoreManager().config.init().value.get('store'),
        });

      let rootFolderId: string | undefined;

      // Create folder structure if hierarchy exists and OrganizeService is available
      if (
        folderHierarchy &&
        organizeService &&
        folderHierarchy.children.size > 0
      ) {
        try {
          const { folderId, docLinks } = createFolderStructure(
            organizeService,
            folderHierarchy,
            null,
            explorerIconService
          );
          rootFolderId = folderId || undefined;

          // Create links for all documents to their respective folders
          for (const { folderId, docId } of docLinks) {
            const folder =
              organizeService.folderTree.folderNode$(folderId).value;
            if (folder) {
              const index = folder.indexAt('after');
              folder.createLink('doc', docId, index);
            }
          }
        } catch (error) {
          logger.warn('Failed to create folder structure:', error);
          // Continue with import even if folder creation fails
        }
      }

      return {
        docIds: pageIds,
        entryId,
        isWorkspaceFile,
        rootFolderId,
      };
    },
  },
  notionEnhanced: {
    fileOptions: { acceptType: 'Zip', multiple: false },
    importFunction: async (
      _docCollection,
      _files,
      _handleImportAffineFile,
      _organizeService,
      _explorerIconService
    ) => {
      // Notion Enhanced import is handled via preview flow (handleNotionImportConfirm)
      // This function is not actually called for notionEnhanced
      return { docIds: [], entryId: undefined };
    },
  },
  csv: {
    fileOptions: { acceptType: 'Csv', multiple: true },
    importFunction: async (
      _docCollection,
      _files,
      _handleImportAffineFile,
      _organizeService,
      _explorerIconService
    ) => {
      // CSV import is handled via preview flow (handleCsvImportConfirm)
      // This function is not actually called for CSV
      return { docIds: [], entryId: undefined };
    },
  },
  excel: {
    fileOptions: { acceptType: 'Excel', multiple: true },
    importFunction: async (
      _docCollection,
      _files,
      _handleImportAffineFile,
      _organizeService,
      _explorerIconService
    ) => {
      // Excel import is handled via preview flow (handleExcelImportConfirm)
      // This function is not actually called for Excel
      return { docIds: [], entryId: undefined };
    },
  },
  docx: {
    fileOptions: { acceptType: 'Docx', multiple: false },
    importFunction: async (docCollection, file) => {
      const files = Array.isArray(file) ? file : [file];
      const docIds: string[] = [];
      for (const file of files) {
        const docId = await DocxTransformer.importDocx({
          collection: docCollection,
          schema: getStreamLainWorkspaceSchema(),
          imported: file,
          extensions: getStoreManager().config.init().value.get('store'),
        });
        if (docId) docIds.push(docId);
      }
      return { docIds };
    },
  },
  snapshot: {
    fileOptions: { acceptType: 'Zip', multiple: false },
    importFunction: async (
      docCollection,
      files,
      _handleImportAffineFile,
      _organizeService,
      _explorerIconService,
      onProgress
    ) => {
      const file = files.length === 1 ? files[0] : null;
      if (!file) {
        throw new Error('Expected a single zip file for snapshot import');
      }
      const importedSystemDocIds = new Set<string>();
      const importedStores = (
        await ZipTransformer.importDocs(
          docCollection,
          getStreamLainWorkspaceSchema(),
          file,
          {
            beforeYDocArchiveDocsLoaded: async targets => {
              const bindings = targets.flatMap(
                target => target.databaseSystemDocAccess
              );
              for (const target of targets) {
                if (target.system) {
                  importedSystemDocIds.add(target.targetDocId);
                }
              }
              for (const binding of bindings) {
                importedSystemDocIds.add(binding.rootDocId);
                for (const shard of binding.manifest.shards) {
                  importedSystemDocIds.add(shard.serverDocId);
                }
              }
              await preflightSnapshotImportDocs({
                workspaceId: docCollection.id,
                pageDocIds: targets
                  .filter(target => !target.system)
                  .map(target => target.targetDocId),
                systemDocIds: [...importedSystemDocIds],
              });
              if (bindings.length === 0) return;
              await postDatabaseSystemDocAccessBindings({
                workspaceId: docCollection.id,
                bindings,
              });
            },
            onYDocArchiveProgress: ({ processedDocs, totalDocs }) => {
              onProgress?.({
                phase: 'snapshot-importing',
                processedRows: processedDocs,
                totalRows: totalDocs,
                percent:
                  totalDocs > 0
                    ? Math.floor((processedDocs / totalDocs) * 100)
                    : null,
              });
            },
          }
        )
      ).filter((doc): doc is Store => doc !== undefined);
      normalizeSnapshotImportStores(importedStores);
      await registerImportedDatabaseSystemDocAccess(
        docCollection.id,
        importedStores
      );
      const docIds = snapshotImportPageDocIds(importedStores);
      const systemDocIds = [
        ...new Set([
          ...snapshotImportSystemDocIds(importedStores),
          ...importedSystemDocIds,
        ]),
      ];

      return {
        docIds,
        systemDocIds,
      };
    },
  },
  dotaffinefile: {
    fileOptions: { acceptType: 'Skip', multiple: false },
    importFunction: async (
      _,
      __,
      handleImportAffineFile,
      _organizeService,
      _explorerIconService
    ) => {
      await handleImportAffineFile();
      return {
        docIds: [],
        entryId: undefined,
        isWorkspaceFile: true,
      };
    },
  },
};

const ImportOptionItem = ({
  label,
  prefixIcon,
  suffixIcon,
  suffixTooltip,
  type,
  onImport,
  ...props
}: {
  label: string;
  prefixIcon: ReactElement<SVGAttributes<SVGElement>>;
  suffixIcon?: ReactElement<SVGAttributes<SVGElement>>;
  suffixTooltip?: string;
  type: ImportType;
  onImport: (type: ImportType) => void;
}) => {
  const t = useI18n();
  return (
    <div className={style.importItem} onClick={() => onImport(type)} {...props}>
      {prefixIcon}
      <div className={style.importItemLabel}>{t[label]()}</div>
      {suffixIcon && (
        <IconButton
          className={style.importItemSuffix}
          icon={suffixIcon}
          tooltip={suffixTooltip ? t[suffixTooltip]() : undefined}
        />
      )}
    </div>
  );
};

const ImportOptions = ({
  onImport,
}: {
  onImport: (type: ImportType) => void;
}) => {
  const t = useI18n();

  return (
    <>
      <div className={style.importModalTitle}>{t['Import']()}</div>
      <div className={style.importModalContent}>
        {importOptions.map(
          ({
            key,
            label,
            prefixIcon,
            suffixIcon,
            suffixTooltip,
            testId,
            type,
          }) => (
            <ImportOptionItem
              key={key}
              prefixIcon={prefixIcon}
              suffixIcon={suffixIcon}
              suffixTooltip={suffixTooltip}
              label={label}
              type={type}
              onImport={onImport}
              data-testid={testId}
            />
          )
        )}
      </div>
      <div className={style.importModalTip}>
        {t['com.streamlain.import.modal.tip.no-discord']()}
      </div>
    </>
  );
};

const terminalImportJobStatuses = new Set(['completed', 'failed', 'cancelled']);

function pendingFrontendImportProgress(
  phase: FrontendImportProgress['phase'],
  totalRows = 0
): FrontendImportProgress {
  return {
    phase,
    processedRows: 0,
    totalRows,
    percent: null,
  };
}

function waitForImportStatusPaint(): Promise<void> {
  return new Promise(resolve => {
    if (typeof requestAnimationFrame === 'function') {
      requestAnimationFrame(() => resolve());
      return;
    }
    setTimeout(resolve, 0);
  });
}

function importErrorMessage(
  error: unknown,
  t: ReturnType<typeof useI18n>
): string {
  if (error instanceof Error && error.name === 'AbortError') {
    return t['com.streamlain.import.status.failed.message.cancelled']();
  }
  if (error instanceof DatabaseImportParserWorkerUnavailableError) {
    return t[
      'com.streamlain.import.status.failed.message.parser-unavailable'
    ]();
  }
  if (error instanceof HTMLImportTooComplexError) {
    return t['com.streamlain.import.status.failed.message.html-too-complex']();
  }
  if (
    error instanceof Error &&
    error.message.toLowerCase() === 'doc already connected'
  ) {
    return t[
      'com.streamlain.import.status.failed.message.target-doc-unavailable'
    ]();
  }
  return error instanceof Error
    ? error.message
    : t['com.streamlain.import.status.failed.message']();
}

type ImportProgressView = {
  phase: FrontendImportProgress['phase'] | 'jobs-waiting' | null;
  percent: number | null;
  processedRows: number;
  totalRows: number;
};

function databaseImportProgress(jobs: DatabaseImportJob[]): ImportProgressView {
  if (jobs.length === 0) {
    return { phase: null, percent: null, processedRows: 0, totalRows: 0 };
  }
  const totalRows = jobs.reduce((sum, job) => sum + job.totalRows, 0);
  const processedRows = jobs.reduce((sum, job) => sum + job.processedRows, 0);
  if (totalRows <= 0) {
    return {
      phase: 'jobs-waiting',
      percent: null,
      processedRows,
      totalRows,
    };
  }
  const percent = Math.min(100, Math.floor((processedRows / totalRows) * 100));
  return {
    phase: 'rows-importing',
    percent,
    processedRows,
    totalRows,
  };
}

function importProgressLabel(
  progress: ImportProgressView,
  t: ReturnType<typeof useI18n>
): string {
  switch (progress.phase) {
    case 'csv-reading':
      return t['com.streamlain.import.status.progress.csvReading']();
    case 'excel-reading':
      return t['com.streamlain.import.status.progress.excelReading']();
    case 'notion-reading':
      return t['com.streamlain.import.status.progress.notionReading']();
    case 'snapshot-importing':
      return t['com.streamlain.import.status.importing.message']();
    case 'databases-preparing':
      return t['com.streamlain.import.status.progress.databasesPreparing']();
    case 'rows-importing':
      return t['com.streamlain.import.status.progress.rowsImporting']();
    case 'import-finalizing':
      return t['com.streamlain.import.status.progress.finalizing']();
    case 'import-cancelling':
      return t['com.streamlain.import.status.progress.cancelling']();
    case 'jobs-waiting':
      return t['com.streamlain.import.status.progress.jobsWaiting']();
    default:
      return t['com.streamlain.import.status.importing.message']();
  }
}

function importRowProgressText(
  progress: ImportProgressView,
  t: ReturnType<typeof useI18n>
): string | null {
  if (progress.totalRows <= 0) return null;
  const formatter = new Intl.NumberFormat();
  return t['com.streamlain.import.status.progress.rows']({
    processedRows: formatter.format(progress.processedRows),
    totalRows: formatter.format(progress.totalRows),
  });
}

const ImportProgressToast = ({
  jobs,
  frontendProgress,
  onClose,
  cancelling,
}: {
  jobs: DatabaseImportJob[];
  frontendProgress: FrontendImportProgress | null;
  onClose: () => void;
  cancelling: boolean;
}) => {
  const t = useI18n();
  const formatter = useMemo(() => new Intl.NumberFormat(), []);
  const progress =
    jobs.length > 0
      ? databaseImportProgress(jobs)
      : (frontendProgress ?? {
          phase: null,
          percent: null,
          processedRows: 0,
          totalRows: 0,
        });
  const progressLabel = importProgressLabel(progress, t);
  const rowProgressText = importRowProgressText(progress, t);
  const canCancel =
    jobs.some(job => !terminalImportJobStatuses.has(job.status)) ||
    (frontendProgress !== null &&
      frontendProgress.phase !== 'snapshot-importing');
  return (
    <div className={style.importToastLayer}>
      <div
        className={style.importProgressToast}
        role="status"
        aria-live="polite"
        aria-label={rowProgressText ?? progressLabel}
      >
        <span className={style.importToastCount}>
          {progress.totalRows > 0
            ? formatter.format(progress.processedRows)
            : progressLabel}
        </span>
        <div className={style.importToastProgressTrack}>
          <div
            className={
              progress.percent === null
                ? style.importToastProgressIndicatorIndeterminate
                : style.importToastProgressIndicator
            }
            style={
              progress.percent === null
                ? undefined
                : { width: `${progress.percent}%` }
            }
          />
        </div>
        <span
          className={style.importToastCount}
          aria-hidden={progress.totalRows <= 0}
        >
          {progress.totalRows > 0 ? formatter.format(progress.totalRows) : ''}
        </span>
        <button
          type="button"
          className={style.importToastCloseButton}
          onClick={onClose}
          disabled={!canCancel || cancelling}
          aria-label={t['Cancel']()}
          title={t['Cancel']()}
        >
          <CloseIcon />
        </button>
      </div>
    </div>
  );
};

const SuccessStatus = ({ onComplete }: { onComplete: () => void }) => {
  const t = useI18n();
  return (
    <div className={style.successStatusWrapper}>
      <div className={style.importModalTitle}>
        {t['com.streamlain.import.status.success.title']()}
      </div>
      <p className={style.importStatusContent}>
        {t['com.streamlain.import.status.success.message']()}
      </p>
      <div className={style.importModalButtonContainer}>
        <Button onClick={onComplete} variant="primary">
          {t['Complete']()}
        </Button>
      </div>
    </div>
  );
};

const ErrorStatus = ({
  error,
  onRetry,
}: {
  error: string | null;
  onRetry: () => void;
}) => {
  const t = useI18n();
  return (
    <div className={style.successStatusWrapper}>
      <div className={style.importModalTitle}>
        {t['com.streamlain.import.status.failed.title']()}
      </div>
      <p className={style.importStatusContent}>
        {error || t['com.streamlain.import.status.failed.message.unknown']()}
      </p>
      <div className={style.importModalButtonContainer}>
        <Button onClick={onRetry} variant="primary">
          {t['Retry']()}
        </Button>
      </div>
    </div>
  );
};

export const ImportDialog = ({
  close,
  files: initialFiles,
  importType: initialImportType,
  targetDocId,
  dropPosition,
}: DialogComponentProps<WORKSPACE_DIALOG_SCHEMA['import']>) => {
  const t = useI18n();
  const [status, setStatus] = useState<Status>('idle');
  const [importError, setImportError] = useState<string | null>(null);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [databaseImportJobs, setDatabaseImportJobs] = useState<
    DatabaseImportJob[]
  >([]);
  const [frontendImportProgress, setFrontendImportProgress] =
    useState<FrontendImportProgress | null>(null);
  const [cancellingImport, setCancellingImport] = useState(false);
  const activeLocalImportAbortRef = useRef<AbortController | null>(null);
  const initialImportRunKeyRef = useRef<string | null>(null);
  const workspace = useService(WorkspaceService).workspace;
  const canCreateDoc = useGuard('Workspace_CreateDoc');
  const canWriteBlobs = useGuard('Workspace_Blobs_Write');
  const canImport =
    workspace.flavour === 'local' ||
    (canCreateDoc === true && canWriteBlobs === true);
  const docCollection = workspace.docCollection;
  const docsService = useService(DocsService);
  const importRuntime = databaseImportRuntimeFromWorkspaceFlavour(
    workspace.flavour
  );
  const useDatabaseImportJob =
    resolveDatabaseImportRoute(importRuntime) === 'database-import-job';
  const organizeService = useService(OrganizeService);
  const explorerIconService = useService(ExplorerIconService);
  const storeExtensions = useMemo(
    () => getStoreManager().config.init().value.get('store'),
    []
  );

  const startLocalImportRun = useCallback(
    (phase: FrontendImportProgress['phase'], estimatedTotalRows = 0) => {
      activeLocalImportAbortRef.current?.abort();
      const controller = new AbortController();
      activeLocalImportAbortRef.current = controller;
      setDatabaseImportJobs([]);
      setCancellingImport(false);
      setFrontendImportProgress(
        pendingFrontendImportProgress(phase, estimatedTotalRows)
      );
      return controller;
    },
    []
  );

  const isActiveLocalImportRun = useCallback((controller: AbortController) => {
    return activeLocalImportAbortRef.current === controller;
  }, []);

  const finishLocalImportRun = useCallback((controller: AbortController) => {
    if (activeLocalImportAbortRef.current !== controller) return;
    activeLocalImportAbortRef.current = null;
    setFrontendImportProgress(null);
  }, []);

  // E024-S001-T005: Get target doc for drag & drop import
  const targetDoc = targetDocId ? docCollection.getDoc(targetDocId) : undefined;
  const initialDatabaseBlockIdsRef = useRef<Set<string> | null>(null);
  if (initialDatabaseBlockIdsRef.current === null) {
    initialDatabaseBlockIdsRef.current = new Set(
      targetDoc
        ?.getStore()
        .getBlocksByFlavour('affine:database')
        .map(block => block.id) ?? []
    );
  }

  const collectImportedDatabaseBlockIds = useCallback(
    (result: DatabaseImportJobResult | DatabaseImportLocalFirstResult) => {
      if (isDatabaseImportLocalFirstResult(result)) {
        return result.databaseBlockIds;
      }
      const initialIds =
        initialDatabaseBlockIdsRef.current ?? new Set<string>();
      return (
        targetDoc
          ?.getStore()
          .getBlocksByFlavour('affine:database')
          .map(block => block.id)
          .filter(blockId => !initialIds.has(blockId)) ?? []
      );
    },
    [targetDoc]
  );

  // E013-S003: State for Notion preview
  const [notionFile, setNotionFile] = useState<File | null>(null);

  // E024-S001: State for CSV preview
  const [csvFiles, setCsvFiles] = useState<File[] | null>(null);

  // E024-S002: State for Excel preview
  const [excelFiles, setExcelFiles] = useState<File[] | null>(null);

  useEffect(() => {
    if (canCreateDoc === false || canWriteBlobs === false) {
      close(undefined);
    }
  }, [canCreateDoc, canWriteBlobs, close]);

  const globalDialogService = useService(GlobalDialogService);

  const { jumpToPage } = useNavigateHelper();
  const handleCreatedWorkspace = useCallback(
    (payload: { metadata: WorkspaceMetadata; defaultDocId?: string }) => {
      if (document.startViewTransition) {
        document.startViewTransition(() => {
          if (payload.defaultDocId) {
            jumpToPage(payload.metadata.id, payload.defaultDocId);
          } else {
            jumpToPage(payload.metadata.id, 'all');
          }
          return new Promise(resolve =>
            setTimeout(resolve, 150)
          ); /* start transition after 150ms */
        });
      } else {
        if (payload.defaultDocId) {
          jumpToPage(payload.metadata.id, payload.defaultDocId);
        } else {
          jumpToPage(payload.metadata.id, 'all');
        }
      }
    },
    [jumpToPage]
  );

  const handleImportAffineFile = useMemo(() => {
    return async () => {
      track.$.navigationPanel.workspaceList.createWorkspace({
        control: 'import',
      });

      return new Promise<WorkspaceMetadata | undefined>((resolve, reject) => {
        globalDialogService.open('import-workspace', undefined, payload => {
          if (payload) {
            handleCreatedWorkspace({ metadata: payload.workspace });
            resolve(payload.workspace);
          } else {
            reject(
              new Error(
                t['com.streamlain.import.status.failed.message.no-workspace']()
              )
            );
          }
        });
      });
    };
  }, [globalDialogService, handleCreatedWorkspace, t]);

  const runImportWithFiles = useAsyncCallback(
    async (type: ImportType, files: File[]) => {
      setImportError(null);
      try {
        const importConfig = importConfigs[type];
        const { acceptType } = importConfig.fileOptions;

        if (!files || (files.length === 0 && acceptType !== 'Skip')) {
          throw new Error(
            t['com.streamlain.import.status.failed.message.no-file-selected']()
          );
        }

        // E013-S003: For Notion Enhanced imports, show preview first
        // Standard Notion import goes directly to import (no Relations)
        if (type === 'notionEnhanced' && files.length === 1) {
          setNotionFile(files[0]);
          setStatus('notionPreview');
          return;
        }

        // E024-S001: For CSV imports, show preview first
        if (type === 'csv' && files.length > 0) {
          setCsvFiles(files);
          setStatus('csvPreview');
          return;
        }

        // E024-S002: For Excel imports, show preview first
        if (type === 'excel' && files.length > 0) {
          setExcelFiles(files);
          setStatus('excelPreview');
          return;
        }

        if (acceptType !== 'Skip') {
          if (type === 'snapshot') {
            setFrontendImportProgress(
              pendingFrontendImportProgress('snapshot-importing')
            );
          }
          setStatus('importing');
          track.$.importModal.$.import({
            type,
            status: 'importing',
          });
        }

        const { docIds, systemDocIds, entryId, isWorkspaceFile, rootFolderId } =
          await importConfig.importFunction(
            docCollection,
            files,
            handleImportAffineFile,
            organizeService,
            explorerIconService,
            setFrontendImportProgress
          );

        if (type === 'snapshot' && workspace.flavour !== 'local') {
          await materializeSnapshotImport({
            docEngine: workspace.engine.doc,
            docsService,
            pageDocIds: docIds,
            systemDocIds,
            workspaceId: workspace.id,
          });
        }

        setImportResult({ docIds, entryId, isWorkspaceFile, rootFolderId });
        setStatus('success');
        track.$.importModal.$.import({
          type,
          status: 'success',
          result: {
            docCount: docIds.length,
          },
        });
        track.$.importModal.$.createDoc({
          control: 'import',
        });
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : t['com.streamlain.import.status.failed.message.unknown']();
        setImportError(errorMessage);
        setStatus('error');
        track.$.importModal.$.import({
          type,
          status: 'failed',
          error: errorMessage || undefined,
        });
        logger.error('Failed to import', error);
      }
    },
    [
      docCollection,
      docsService,
      explorerIconService,
      handleImportAffineFile,
      organizeService,
      t,
      workspace,
    ]
  );

  const handleImport = useAsyncCallback(
    async (type: ImportType) => {
      try {
        const importConfig = importConfigs[type];
        const { acceptType, multiple } = importConfig.fileOptions;

        const files =
          acceptType === 'Skip'
            ? []
            : await openFilesWith(acceptType, multiple);

        return runImportWithFiles(type, files ?? []);
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : t['com.streamlain.import.status.failed.message.unknown']();
        setImportError(errorMessage);
        setStatus('error');
        track.$.importModal.$.import({
          type,
          status: 'failed',
          error: errorMessage || undefined,
        });
        logger.error('Failed to import', error);
      }
    },
    [runImportWithFiles, t]
  );

  // E024-S001-T005 / E024-S002-T006: Handle pre-selected files from drag & drop
  useEffect(() => {
    if (!initialFiles?.length || !initialImportType) return;

    const importKey = [
      initialImportType,
      ...initialFiles.map(
        file => `${file.name}:${file.size}:${file.lastModified}`
      ),
    ].join('|');

    if (initialImportRunKeyRef.current === importKey) return;
    initialImportRunKeyRef.current = importKey;

    void runImportWithFiles(initialImportType, initialFiles);
  }, [initialFiles, initialImportType, runImportWithFiles]);

  const resolveDatabaseImportDocId = useCallback(
    async (config: { existingDocId?: string; newDocName: string }) => {
      if (targetDoc) {
        return targetDoc.id;
      }
      if (config.existingDocId) {
        const existingDoc = docCollection.getDoc(config.existingDocId);
        if (!existingDoc) {
          throw new Error(
            t[
              'com.streamlain.import.status.failed.message.target-doc-unavailable'
            ]()
          );
        }
        return existingDoc.id;
      }

      const record = docsService.createDoc({
        primaryMode: 'edgeless',
        title: config.newDocName,
        createInWorkspaceRoot: true,
      });
      await docsService.changeDocTitle(record.id, config.newDocName);
      return record.id;
    },
    [docCollection, docsService, targetDoc, t]
  );

  const finalizeLocalFirstDatabaseImport = useCallback(
    async (
      result: DatabaseImportLocalFirstResult,
      signal: AbortSignal | undefined
    ) => {
      await flushLocalFirstDatabaseImport({
        collection: docCollection,
        docEngine: workspace.engine.doc,
        docId: result.docId,
        extensions: storeExtensions,
        result,
        signal,
        workspaceId: workspace.id,
      });
      const confirmed = await docsService.waitForServerConfirmed(result.docId, {
        timeoutMs: 15_000,
      });
      if (!confirmed) {
        throw new Error(
          `Imported document "${result.docId}" was not confirmed by the server.`
        );
      }
    },
    [
      docCollection,
      docsService,
      storeExtensions,
      workspace.engine.doc,
      workspace.id,
    ]
  );

  // E013-S007: Handle Notion import after preview confirmation
  const handleNotionImportConfirm = useAsyncCallback(
    async (config: NotionImportConfig) => {
      if (!notionFile) return;

      const localImportController =
        config.importMode === 'edgeless'
          ? startLocalImportRun('notion-reading')
          : null;
      setStatus('importing');
      setImportError(null);
      track.$.importModal.$.import({
        type: 'notion',
        status: 'importing',
      });
      await waitForImportStatusPaint();

      try {
        console.log('[NOTION IMPORT] Mode:', config.importMode);

        if (config.importMode === 'edgeless') {
          if (!localImportController) {
            throw new Error('Local Notion import controller is missing.');
          }
          // E013: Use new standalone NotionImporter for edgeless + relations
          console.log('[NOTION IMPORT] Using NotionImporter (edgeless mode)');

          const importer = NotionImporter.create({
            schema: getStreamLainWorkspaceSchema(),
            extensions: getStoreManager().config.init().value.get('store'),
          });

          setDatabaseImportJobs([]);
          setCancellingImport(false);
          const docId = await resolveDatabaseImportDocId({
            existingDocId: config.existingDocId ?? undefined,
            newDocName: config.newDocName,
          });
          throwIfLocalDatabaseImportAborted(localImportController.signal);
          const result = await importer.import({
            workspaceId: workspace.id,
            docId,
            file: notionFile,
            collection: docCollection,
            docName: config.newDocName,
            dropPosition,
            onJobsChanged: setDatabaseImportJobs,
            onProgress: setFrontendImportProgress,
            signal: localImportController.signal,
          });

          console.log('[NOTION IMPORT] Complete:', result.stats);

          setImportResult({
            docIds: [result.docId],
            entryId: result.docId,
            isWorkspaceFile: false,
            rootFolderId: undefined,
          });
        } else {
          // Standard: Use AFFiNE built-in import
          console.log('[NOTION IMPORT] Using Standard Import');

          const standardResult = await importConfigs.notion.importFunction(
            docCollection,
            [notionFile],
            handleImportAffineFile,
            organizeService,
            explorerIconService
          );

          setImportResult({
            docIds: standardResult.docIds,
            entryId: standardResult.entryId,
            isWorkspaceFile: standardResult.isWorkspaceFile,
            rootFolderId: standardResult.rootFolderId,
          });
        }

        setStatus('success');
        track.$.importModal.$.import({
          type: 'notion',
          status: 'success',
        });
        track.$.importModal.$.createDoc({
          control: 'import',
        });
      } catch (error) {
        if (
          localImportController &&
          !isActiveLocalImportRun(localImportController)
        ) {
          return;
        }
        console.error('[NOTION IMPORT] FAILED! Error:', error);
        console.error(
          '[NOTION IMPORT] Error stack:',
          error instanceof Error ? error.stack : 'no stack'
        );
        const errorMessage = importErrorMessage(error, t);
        setImportError(errorMessage);
        setStatus('error');
        track.$.importModal.$.import({
          type: 'notion',
          status: 'failed',
          error: errorMessage || undefined,
        });
        logger.error('Failed to import Notion', error);
      } finally {
        if (localImportController) {
          const wasActiveImportRun = isActiveLocalImportRun(
            localImportController
          );
          finishLocalImportRun(localImportController);
          if (wasActiveImportRun) {
            setCancellingImport(false);
          }
        }
      }
    },
    [
      notionFile,
      docCollection,
      dropPosition,
      handleImportAffineFile,
      organizeService,
      explorerIconService,
      resolveDatabaseImportDocId,
      startLocalImportRun,
      isActiveLocalImportRun,
      finishLocalImportRun,
      t,
      workspace.id,
    ]
  );

  // E013-S003: Cancel Notion preview
  const handleNotionPreviewCancel = useCallback(() => {
    setNotionFile(null);
    setStatus('idle');
  }, []);

  // E024-S001: Handle CSV import after preview confirmation
  const handleCsvImportConfirm = useAsyncCallback(
    async (config: CsvImportConfig) => {
      if (!csvFiles) return;

      const localImportController = useDatabaseImportJob
        ? null
        : startLocalImportRun('csv-reading', config.estimatedTotalRows);
      setStatus('importing');
      setImportError(null);
      setDatabaseImportJobs([]);
      setCancellingImport(false);
      track.$.importModal.$.import({
        type: 'csv',
        status: 'importing',
      });
      await waitForImportStatusPaint();

      try {
        const docId = await resolveDatabaseImportDocId(config);
        if (localImportController) {
          throwIfLocalDatabaseImportAborted(localImportController.signal);
        }
        const result = useDatabaseImportJob
          ? await importCsvFilesWithDatabaseImportJob({
              workspaceId: workspace.id,
              docId,
              files: config.files,
              hasHeader: config.hasHeader,
              titleColumnIndex: config.titleColumnIndex,
              dropPosition,
              onJobsChanged: setDatabaseImportJobs,
            })
          : await importCsvFilesLocalFirst({
              collection: docCollection,
              docId,
              docName: config.newDocName,
              extensions: storeExtensions,
              files: config.files,
              hasHeader: config.hasHeader,
              titleColumnIndex: config.titleColumnIndex,
              estimatedTotalRows: config.estimatedTotalRows,
              dropPosition,
              onProgress: setFrontendImportProgress,
              signal: localImportController?.signal,
            });

        if (isDatabaseImportLocalFirstResult(result)) {
          await finalizeLocalFirstDatabaseImport(
            result,
            localImportController?.signal
          );
        }

        setImportResult({
          docIds: [result.docId],
          databaseBlockIds: collectImportedDatabaseBlockIds(result),
          entryId: result.docId,
          isWorkspaceFile: false,
        });

        setStatus('success');
        track.$.importModal.$.import({
          type: 'csv',
          status: 'success',
          result: {
            docCount: 1,
          },
        });
      } catch (error) {
        if (
          localImportController &&
          !isActiveLocalImportRun(localImportController)
        ) {
          return;
        }
        const errorMessage = importErrorMessage(error, t);
        setImportError(errorMessage);
        setStatus('error');
        track.$.importModal.$.import({
          type: 'csv',
          status: 'failed',
          error: errorMessage,
        });
        logger.error('Failed to import CSV', error);
      } finally {
        if (localImportController) {
          const wasActiveImportRun = isActiveLocalImportRun(
            localImportController
          );
          finishLocalImportRun(localImportController);
          if (wasActiveImportRun) {
            setCancellingImport(false);
          }
        }
      }
    },
    [
      csvFiles,
      docCollection,
      dropPosition,
      workspace.id,
      resolveDatabaseImportDocId,
      useDatabaseImportJob,
      startLocalImportRun,
      isActiveLocalImportRun,
      finishLocalImportRun,
      finalizeLocalFirstDatabaseImport,
      collectImportedDatabaseBlockIds,
      storeExtensions,
      t,
    ]
  );

  // E024-S001: Cancel CSV preview
  const handleCsvPreviewCancel = useCallback(() => {
    setCsvFiles(null);
    setStatus('idle');
  }, []);

  // E024-S002: Handle Excel import after preview confirmation
  const handleExcelImportConfirm = useAsyncCallback(
    async (config: ExcelImportConfig) => {
      if (!excelFiles) return;

      const localImportController = useDatabaseImportJob
        ? null
        : startLocalImportRun('excel-reading', config.estimatedTotalRows);
      setStatus('importing');
      setImportError(null);
      setDatabaseImportJobs([]);
      setCancellingImport(false);
      track.$.importModal.$.import({
        type: 'excel',
        status: 'importing',
      });
      await waitForImportStatusPaint();

      try {
        const docId = await resolveDatabaseImportDocId(config);
        if (localImportController) {
          throwIfLocalDatabaseImportAborted(localImportController.signal);
        }
        const result = useDatabaseImportJob
          ? await importExcelFilesWithDatabaseImportJob({
              workspaceId: workspace.id,
              docId,
              files: config.files,
              selectedSheets: config.selectedSheets,
              hasHeader: config.hasHeader,
              titleColumnIndex: config.titleColumnIndex,
              dropPosition,
              onJobsChanged: setDatabaseImportJobs,
            })
          : await importExcelFilesLocalFirst({
              collection: docCollection,
              docId,
              docName: config.newDocName,
              extensions: storeExtensions,
              files: config.files,
              selectedSheets: config.selectedSheets,
              hasHeader: config.hasHeader,
              titleColumnIndex: config.titleColumnIndex,
              estimatedTotalRows: config.estimatedTotalRows,
              dropPosition,
              onProgress: setFrontendImportProgress,
              signal: localImportController?.signal,
            });

        if (isDatabaseImportLocalFirstResult(result)) {
          await finalizeLocalFirstDatabaseImport(
            result,
            localImportController?.signal
          );
        }

        setImportResult({
          docIds: [result.docId],
          databaseBlockIds: collectImportedDatabaseBlockIds(result),
          entryId: result.docId,
          isWorkspaceFile: false,
        });

        setStatus('success');
        track.$.importModal.$.import({
          type: 'excel',
          status: 'success',
          result: {
            docCount: 1,
          },
        });
      } catch (error) {
        if (
          localImportController &&
          !isActiveLocalImportRun(localImportController)
        ) {
          return;
        }
        const errorMessage = importErrorMessage(error, t);
        setImportError(errorMessage);
        setStatus('error');
        track.$.importModal.$.import({
          type: 'excel',
          status: 'failed',
          error: errorMessage,
        });
        logger.error('Failed to import Excel', error);
      } finally {
        if (localImportController) {
          const wasActiveImportRun = isActiveLocalImportRun(
            localImportController
          );
          finishLocalImportRun(localImportController);
          if (wasActiveImportRun) {
            setCancellingImport(false);
          }
        }
      }
    },
    [
      excelFiles,
      docCollection,
      dropPosition,
      workspace.id,
      resolveDatabaseImportDocId,
      useDatabaseImportJob,
      startLocalImportRun,
      isActiveLocalImportRun,
      finishLocalImportRun,
      finalizeLocalFirstDatabaseImport,
      collectImportedDatabaseBlockIds,
      storeExtensions,
      t,
    ]
  );

  // E024-S002: Cancel Excel preview
  const handleExcelPreviewCancel = useCallback(() => {
    setExcelFiles(null);
    setStatus('idle');
  }, []);

  const handleComplete = useCallback(() => {
    close(importResult || undefined);
  }, [importResult, close]);

  const handleCancelDatabaseImport = useCallback(async () => {
    const activeLocalImport = activeLocalImportAbortRef.current;
    if (activeLocalImport) {
      setCancellingImport(true);
      setFrontendImportProgress(
        pendingFrontendImportProgress('import-cancelling')
      );
      activeLocalImport.abort();
      return;
    }

    const cancellableJobs = databaseImportJobs.filter(
      job => !terminalImportJobStatuses.has(job.status)
    );
    if (cancellableJobs.length === 0) return;

    setCancellingImport(true);
    try {
      const cancelled = await Promise.all(
        cancellableJobs.map(job =>
          cancelDatabaseImportJob(workspace.id, job.id)
        )
      );
      setDatabaseImportJobs(current =>
        current.map(job => {
          const next = cancelled.find(
            cancelledJob => cancelledJob.id === job.id
          );
          return next ?? job;
        })
      );
      setImportError(
        t['com.streamlain.import.status.failed.message.database-cancelled']()
      );
      setStatus('error');
    } catch (error) {
      setImportError(
        error instanceof Error
          ? error.message
          : t[
              'com.streamlain.import.status.failed.message.database-cancel-failed'
            ]()
      );
      setStatus('error');
    } finally {
      setCancellingImport(false);
    }
  }, [databaseImportJobs, t, workspace.id]);

  const handleRetry = useAsyncCallback(async () => {
    setFrontendImportProgress(null);
    const retryableJobs = databaseImportJobs.filter(
      job => job.status === 'failed' || job.status === 'cancelled'
    );
    if (!useDatabaseImportJob || retryableJobs.length === 0 || !importResult) {
      setStatus('idle');
      return;
    }

    setStatus('importing');
    setImportError(null);
    setCancellingImport(false);
    try {
      const retried = await Promise.all(
        retryableJobs.map(job => retryDatabaseImportJob(workspace.id, job.id))
      );
      const nextJobs = databaseImportJobs.map(job => {
        const retriedJob = retried.find(candidate => candidate.id === job.id);
        return retriedJob ?? job;
      });
      setDatabaseImportJobs(nextJobs);
      const completedJobs = await waitForDatabaseImportJobs(
        workspace.id,
        nextJobs,
        setDatabaseImportJobs
      );
      setDatabaseImportJobs(completedJobs);
      setStatus('success');
    } catch (error) {
      setImportError(
        error instanceof Error ? error.message : 'Database import retry failed'
      );
      setStatus('error');
    }
  }, [databaseImportJobs, importResult, useDatabaseImportJob, workspace.id]);

  const statusComponents: Record<Status, React.ReactNode> = {
    idle: <ImportOptions onImport={handleImport} />,
    // E013-S003: Notion preview before import
    notionPreview: notionFile ? (
      <NotionImportPreview
        file={notionFile}
        onImport={handleNotionImportConfirm}
        onCancel={handleNotionPreviewCancel}
        importing={false}
      />
    ) : null,
    // E024-S001: CSV preview before import
    // E024-S001-T006: Pass targetDocId for read-only mode in drag & drop
    csvPreview: csvFiles ? (
      <CsvImportPreview
        files={csvFiles}
        onImport={handleCsvImportConfirm}
        onCancel={handleCsvPreviewCancel}
        importing={false}
        targetDocId={targetDocId}
        targetDocName={targetDoc?.meta?.title}
        useDatabaseImportJob={useDatabaseImportJob}
      />
    ) : null,
    // E024-S002: Excel preview before import
    // E024-S002-T006: Pass targetDocId for read-only mode in drag & drop
    excelPreview: excelFiles ? (
      <ExcelImportPreview
        files={excelFiles}
        onImport={handleExcelImportConfirm}
        onCancel={handleExcelPreviewCancel}
        importing={false}
        targetDocId={targetDocId}
        targetDocName={targetDoc?.meta?.title}
        useDatabaseImportJob={useDatabaseImportJob}
      />
    ) : null,
    importing: null,
    success: <SuccessStatus onComplete={handleComplete} />,
    error: <ErrorStatus error={importError} onRetry={handleRetry} />,
  };

  // E013-S003: Larger modal for preview with proper sizing
  const modalWidth =
    status === 'notionPreview' ||
    status === 'csvPreview' ||
    status === 'excelPreview'
      ? 800
      : 480;

  // Only use minHeight for preview and idle states, not for success/error/importing
  const needsMinHeight =
    status === 'idle' ||
    status === 'notionPreview' ||
    status === 'csvPreview' ||
    status === 'excelPreview';

  if (!canImport) {
    return null;
  }

  if (status === 'importing') {
    return (
      <ImportProgressToast
        jobs={databaseImportJobs}
        frontendProgress={frontendImportProgress}
        onClose={() => {
          void handleCancelDatabaseImport();
        }}
        cancelling={cancellingImport}
      />
    );
  }

  return (
    <Modal
      open
      onOpenChange={(open: boolean) => {
        if (!open) {
          close(importResult || undefined);
        }
      }}
      width={modalWidth}
      contentOptions={{
        ['data-testid' as string]: 'import-modal',
        style: {
          maxHeight: '90vh',
          maxWidth: '90vw',
          minHeight: needsMinHeight ? '400px' : 'auto',
          padding: 0,
          overflow: 'hidden',
          display: 'flex',
          background: cssVarV2('layer/background/primary'),
        },
      }}
      closeButtonOptions={{
        className: style.closeButton,
      }}
    >
      <div className={style.importModalContainer} data-testid="import-dialog">
        {statusComponents[status]}
      </div>
    </Modal>
  );
};
